import Vue from 'vue'
import { S, G, M, A } from './types'
import { Getters, Mutations, Actions } from 'vuex'
import { TeamMember } from '@/types/team'

/* -------------------------------------------------------------------------- */

const state = (): S => ({
  patients: [],
  teamMembers: []
})

/* -------------------------------------------------------------------------- */

const getters: Getters<S, G> = {
  workersForPatientUser: state => {
    //ログインユーザーが patient の場合、patients は１件のみ返却される
    return state.patients.length ? state.patients[0].workers : []
  },

  patients: state => state.patients,
  teamMembers: ({ teamMembers }) => teamMembers,
  isSameTeam: state => (workerId, workerId2) => {
    return !!state.patients.find(p => {
      const list = p.workers.map(w => w.id)
      return list.includes(workerId) && list.includes(workerId2)
    })
  },
  isPatientsTeam: state => (patientId, workerId) => {
    const patient = state.patients.find(p => p.id === patientId)
    if (!patient) return false
    return patient.workers.map(w => w.id).includes(workerId)
  }
}

/* -------------------------------------------------------------------------- */

const mutations: Mutations<S, M> = {
  updatePatients: (state, patients) => {
    state.patients = patients
  },

  setTeamMembers: (state, teamMembers) => {
    state.teamMembers = teamMembers
  },

  clearTeamMembers: state => {
    state.teamMembers = []
  },

  deleteTeamMember: (state, { patientId, workerId }) => {
    const index = state.teamMembers.findIndex(
      member => member.patient === patientId && member.worker.id === workerId
    )

    if (index === -1) {
      return
    }

    state.teamMembers.splice(index, 1)
  },

  replaceTeamMember: (state, updatedTeamMember) => {
    const index = state.teamMembers.findIndex(
      member =>
        member.patient === updatedTeamMember.patient &&
        member.worker.id === updatedTeamMember.worker
    )

    if (index === -1) {
      return
    }

    state.teamMembers.splice(
      index,
      1,
      Object.assign(updatedTeamMember, {
        worker: { ...state.teamMembers[index].worker }
      }) as TeamMember
    )
  }
}

/* -------------------------------------------------------------------------- */

const actions: Actions<S, A, G, M> = {
  updatePatients: async ({ commit }) => {
    const patients = await Vue.$api.team.fetch()
    commit('updatePatients', patients)
  },

  async fetchTeamMembers({ commit }, { patientId }) {
    const response = await Vue.$api.team.fetchTeamMemberList({ patientId })

    commit('setTeamMembers', response)
  },

  async deleteTeamMember({ commit }, { patientId, workerId }) {
    await Vue.$api.team.deleteTeamMember({
      patientId,
      workerId
    })
    commit('deleteTeamMember', { patientId, workerId })
  },

  async patchTeamMember({ commit }, { patientId, workerId, params }) {
    const response = await Vue.$api.team.patchTeamMember({
      patientId,
      workerId,
      params
    })
    commit('replaceTeamMember', response)
  },

  async putTeamMembers({ commit }, { patientId, params }) {
    await Vue.$api.team.put({
      patientId,
      params
    })
    const response = await Vue.$api.team.fetchTeamMemberList({ patientId })
    commit('setTeamMembers', response)
  }
}

/* -------------------------------------------------------------------------- */

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
